import { Canvas, Point, FabricImage, CanvasEvents, type TMat2D } from 'fabric';
import { HelperService } from '../../services/helper.service';
import { EmitterService } from '../../services/emitter.service';
import { Subject, takeUntil } from 'rxjs';
import { BaseDrawingHandler, BaseDrawingHandlerParams } from '../base-class-handler/base-drawing-handler';
import { BaseClassHandler } from '../base-class-handler/base-class-handler';
import { FabricEventGroupManager } from '../base-class-handler/fabric-event-group-manager';
import {
  onAnnotationDirtyStateChanged,
  onAnnotationsStateChanged,
  onDeleteCommentAnnotation,
  onExitingEditingAnnotation,
  onImageResetOrientation,
} from '../../services/annotation.service';
import { AiOptions, AnnotationParams, ExistingAnnotationData } from '@mtm/conversation-client-sdk-annotation';
import { FileTypeService } from '../../services/file-type.service';

type DragCanvas = Canvas & {
  isDragging: boolean;
  lastPosX: number;
  lastPosY: number;
};

type ImageCanvasListenerGroup = 'move' | 'zoom';
type ZoomRange = { min: number; max: number };

export class ImageAnnotationHandler implements BaseClassHandler {
  private static readonly DISPLAY_MIN = 50;
  private static readonly DISPLAY_MAX = 400;

  annotationPlugin: any;
  instanceId: string;
  canvasContainer: any;
  item: any;
  canvasFabric: Canvas;
  canvasZoom: number;
  defaultZoom: number;
  zoomRangePercent: ZoomRange = { min: 25, max: 1000 };
  zoomRangeAbsolute: ZoomRange = { min: 0.25, max: 10 };
  displayZoomRange: ZoomRange = { min: 50, max: 400 };
  defaulViewport: any;
  strokeWidth: number = 4;
  triangleSize: number = 20;
  isImageMovable: boolean = false;
  viewportTransform: any;
  handlerReady: boolean = false;
  drawingHandler: BaseDrawingHandler;
  ngUnsubscribe = new Subject();
  activeAnnotationMessage: ExistingAnnotationData | null = null;
  fileTypeService: FileTypeService;
  private hasUnsavedDrawingState = false;

  private imageMoveMouseDownHandler: (this: DragCanvas, opt: CanvasEvents['mouse:down']) => void;
  private imageMoveMouseMoveHandler: (this: DragCanvas, opt: CanvasEvents['mouse:move']) => void;
  private imageMoveMouseUpHandler: (this: DragCanvas, opt: CanvasEvents['mouse:up']) => void;
  private imageZoomMouseWheelHandler: (this: DragCanvas, opt: CanvasEvents['mouse:wheel']) => void;
  private readonly canvasEventGroups = new FabricEventGroupManager<ImageCanvasListenerGroup>(['move', 'zoom']);
  isAnnotationDisplayEnabled: boolean = true;

  get annotatingMode() {
    return this.annotationPlugin?.controls?.uiState?.adding || false;
  }

  get editingMode() {
    return this.annotationPlugin?.controls?.uiState?.editing || false;
  }

  get isAnnotatingOrEditing() {
    return this.annotatingMode || this.editingMode;
  }

  get hasUnsavedDrawing(): boolean {
    return this.hasUnsavedDrawingState;
  }

  constructor(annotationPlugin: any, item: any, instanceId: string, fileTypeService: FileTypeService) {
    window['imageAnnotationHandler'] = this;
    this.annotationPlugin = annotationPlugin;
    this.item = item;
    this.instanceId = instanceId;
    this.fileTypeService = fileTypeService;
    this.setupEventHandlers();
    this.setup();
    this.isImageMovable = true;

    onDeleteCommentAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.deleteCommentAnnotation(args),
    });
    onImageResetOrientation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (instanceId: string) => {
        if (instanceId !== this.instanceId) {
          return;
        }
        this.centerImage(true);
      },
    });
  }

  setupEventHandlers() {
    const host = this;
    this.imageMoveMouseDownHandler = function (this: DragCanvas, opt: CanvasEvents['mouse:down']) {
      host.onImageMoveMouseDown(this, opt);
    };
    this.imageMoveMouseMoveHandler = function (this: DragCanvas, opt: CanvasEvents['mouse:move']) {
      host.onImageMoveMouseMove(this, opt);
    };
    this.imageMoveMouseUpHandler = function (this: DragCanvas, opt: CanvasEvents['mouse:up']) {
      host.onImageMoveMouseUp(this, opt);
    };
    this.imageZoomMouseWheelHandler = function (this: DragCanvas, opt: CanvasEvents['mouse:wheel']) {
      host.onImageZoomMouseWheel(this, opt);
    };
  }

  onImageMoveMouseDown(canvas: DragCanvas, opt: CanvasEvents['mouse:down']) {
    if (!this.isImageMovable) return;

    const evt = opt.e as PointerEvent;
    canvas.isDragging = true;
    canvas.lastPosX = evt.clientX;
    canvas.lastPosY = evt.clientY;
  }

  onImageMoveMouseMove(canvas: DragCanvas, opt: CanvasEvents['mouse:move']) {
    if (canvas.isDragging) {
      const e = opt.e as PointerEvent;
      const vpt = canvas.viewportTransform;

      const imageWidth = canvas.backgroundImage.width * canvas.getZoom();
      const imageHeight = canvas.backgroundImage.height * canvas.getZoom();

      let extraWidth, extraHeight;

      if (canvas.getZoom() > this.defaultZoom) {
        extraWidth = Math.abs(canvas.width - imageWidth);
        extraHeight = Math.abs(canvas.height - imageHeight);
      } else {
        extraWidth = 0;
        extraHeight = 0;
      }

      vpt[4] += e.clientX - canvas.lastPosX;
      vpt[5] += e.clientY - canvas.lastPosY;
      vpt[4] = Math.max(0 - extraWidth, vpt[4]);
      vpt[4] = Math.min(vpt[4], canvas.width - imageWidth + extraWidth);

      vpt[5] = Math.max(0 - extraHeight, vpt[5]);
      vpt[5] = Math.min(vpt[5], canvas.height - imageHeight + extraHeight);
      canvas.requestRenderAll();
      canvas.lastPosX = e.clientX;
      canvas.lastPosY = e.clientY;
    }
  }

  onImageMoveMouseUp(canvas: DragCanvas, opt: CanvasEvents['mouse:up']) {
    if (canvas.isDragging) {
      canvas.setViewportTransform(canvas.viewportTransform);
      this.viewportTransform = canvas.viewportTransform;
      canvas.isDragging = false;
      this.annotationPlugin?.fire?.('imageViewportChanged', {
        viewportTransform: canvas.viewportTransform.slice(),
        zoom: canvas.getZoom(),
      });
    }
  }

  onImageZoomMouseWheel(canvas: DragCanvas, opt: CanvasEvents['mouse:wheel']) {
    const evt = opt.e;
    // if (evt.altKey !== true) return;

    const minZoom = this.zoomRangeAbsolute.min;
    const maxZoom = this.zoomRangeAbsolute.max;
    const delta = evt.deltaY;
    let zoom = canvas.getZoom();
    zoom *= 0.999 ** delta;
    if (zoom > maxZoom) zoom = maxZoom;
    if (zoom < minZoom) zoom = minZoom;
    this.setCanvasZoom(zoom, evt);
    evt.preventDefault();
    evt.stopPropagation();
  }

  setup() {
    const imageURL = this.fileTypeService.isConvertibleImageFormat(this.item)
      ? this.item.signedURL + '?thumbnail=_IMG'
      : this.item.signedURL;

    // Fabric canvas initialisation
    const canvasId = 'canvas-fabric-image-' + this.instanceId;
    const canvasFabric = new Canvas(canvasId, {
      isDrawingMode: false,
      selection: false,
      stopContextMenu: true, // prevent context menu from showing
    });

    const parentEl = document.querySelector('.vac-canvas-d');
    canvasFabric.setWidth($(parentEl).width());
    canvasFabric.setHeight($(parentEl).height());
    this.canvasFabric = canvasFabric;
    this.canvasEventGroups.setCanvas(canvasFabric);

    FabricImage.fromURL(imageURL).then((img) => {
      img.set({
        excludeFromExport: true,
        selectable: false,
        hoverCursor: 'cursor',
      });
      this.canvasFabric.backgroundImage = img;
      this.canvasFabric.renderAll();
      this.centerImage(true);
      this.canvasFabric.clipPath = this.canvasFabric.backgroundImage;
      this.strokeWidth = this.calculateStrokeWidth();
      this.triangleSize = this.calculateTriangleSize();
      this.setupDrawingHandler(imageURL);
      this.enableImageMove();
      this.enableImageZoom();
      this.registerListeners();
      this.handlerReady = true;
    });
  }

  setupDrawingHandler(imageURL) {
    const baseDrawingHandlerParams: BaseDrawingHandlerParams = {
      canvasFabric: this.canvasFabric,
      strokeWidth: this.strokeWidth,
      triangleSize: this.triangleSize,
      imageURL: imageURL,
      instanceId: this.instanceId,
      annotationPlugin: this.annotationPlugin,
    };

    this.drawingHandler = new BaseDrawingHandler(baseDrawingHandlerParams);
  }

  /**
   * Centers the image in the canvas and calculates appropriate zoom level
   * @param reset If true, recalculates zoom level. If false, uses existing zoom
   */
  centerImage(reset = false) {
    const { canvasFabric } = this;
    const img = canvasFabric.backgroundImage;
    let zoom = canvasFabric.getZoom();

    if (this.canvasZoom && !reset) {
      zoom = this.canvasZoom;
    } else {
      if (!img || !canvasFabric) return;

      const widthRatio = canvasFabric.width / img.width;
      const heightRatio = canvasFabric.height / img.height;

      // Use the smaller ratio to ensure the image fits within the canvas
      zoom = Math.min(widthRatio, heightRatio);

      // Center the viewport
      this.centerViewport();
      this.defaultZoom = zoom;
      this.updateZoomRanges();
      this.defaulViewport = this.canvasFabric.viewportTransform.slice(); // Clone the array
    }

    // Apply the calculated zoom
    this.setCanvasZoom(zoom);
    this.canvasZoom = zoom;
  }

  centerViewport() {
    const backImg = this.canvasFabric.backgroundImage;

    if (!backImg) return;

    // Calculate the center of the canvas
    const canvasCenter = new Point(this.canvasFabric.width / 2, this.canvasFabric.height / 2);

    // Calculate the center of the image
    const imageCenter = new Point(backImg.width / 2, backImg.height / 2);

    // Calculate the offset needed to center the image
    const offsetX = canvasCenter.x - imageCenter.x;
    const offsetY = canvasCenter.y - imageCenter.y;

    // Update the viewport transform to center the image
    const vpt = this.canvasFabric.viewportTransform;

    // Reset the scale and rotation components
    vpt[0] = 1; // Scale X
    vpt[3] = 1; // Scale Y
    vpt[1] = 0; // Skew X
    vpt[2] = 0; // Skew Y

    // Set the translation components
    vpt[4] = offsetX;
    vpt[5] = offsetY;

    this.canvasFabric.setViewportTransform(vpt);
  }

  setCanvasZoom(zoom, pointer: any = null) {
    let imageCenterPoint;

    if (pointer) {
      imageCenterPoint = { x: pointer.offsetX, y: pointer.offsetY };
    } else {
      imageCenterPoint = new Point(this.canvasFabric.width / 2, this.canvasFabric.height / 2);
    }

    this.canvasFabric.zoomToPoint(imageCenterPoint, zoom);
    this.canvasZoom = zoom;
    this.annotationPlugin?.fire?.('imageZoomChanged', {
      zoom: this.canvasZoom,
      viewportTransform: this.canvasFabric.viewportTransform.slice(),
    });
  }

  /**
   * Apply canvas transform from compare sync without re-emitting plugin events (avoids feedback loops).
   */
  applySyncedViewState(viewportTransform: number[]): void {
    if (!this.handlerReady || !this.canvasFabric || viewportTransform.length < 6) {
      return;
    }
    const v = viewportTransform.slice(0, 6) as TMat2D;
    this.canvasFabric.setViewportTransform(v);
    this.canvasZoom = this.canvasFabric.getZoom();
    this.viewportTransform = v;
    this.canvasFabric.requestRenderAll();
  }

  /** Snapshot of the Fabric viewport matrix for compare sync (six affine components). */
  getViewportTransformForSync(): number[] | null {
    if (!this.handlerReady || !this.canvasFabric?.viewportTransform) {
      return null;
    }
    const v = this.canvasFabric.viewportTransform;
    if (v.length < 6) {
      return null;
    }
    return v.slice(0, 6);
  }

  /** Recomputes actual zoom limits (varies by image) and the fixed display range (50–400%). */
  private updateZoomRanges(): void {
    const defaultZoom = this.defaultZoom || 1;
    const minMultiplier = 0.25;
    const maxMultiplier = defaultZoom < 1 ? 10 : 4;

    this.zoomRangePercent = {
      min: Math.round(minMultiplier * 100),
      max: Math.round(maxMultiplier * 100),
    };
    this.zoomRangeAbsolute = {
      min: minMultiplier * defaultZoom,
      max: maxMultiplier * defaultZoom,
    };

    this.displayZoomRange = {
      min: ImageAnnotationHandler.DISPLAY_MIN,
      max: ImageAnnotationHandler.DISPLAY_MAX,
    };
  }

  /** Converts a baseline-relative to a fixed display percentage. */
  actualToDisplayPercent(actualPercent: number): number {
    const { min: actualMin, max: actualMax } = this.zoomRangePercent;
    const { DISPLAY_MIN, DISPLAY_MAX } = ImageAnnotationHandler;

    if (actualPercent <= 100) {
      const actualSpan = 100 - actualMin;
      if (actualSpan <= 0) return 100;
      return DISPLAY_MIN + ((actualPercent - actualMin) / actualSpan) * (100 - DISPLAY_MIN);
    }
    const actualSpan = actualMax - 100;
    if (actualSpan <= 0) return 100;
    return 100 + ((actualPercent - 100) / actualSpan) * (DISPLAY_MAX - 100);
  }

  /** Inverse of actualToDisplayPercent — converts a display % back to a baseline-relative %. */
  displayToActualPercent(displayPercent: number): number {
    const { min: actualMin, max: actualMax } = this.zoomRangePercent;
    const { DISPLAY_MIN, DISPLAY_MAX } = ImageAnnotationHandler;

    if (displayPercent <= 100) {
      const displaySpan = 100 - DISPLAY_MIN;
      if (displaySpan <= 0) return 100;
      return actualMin + ((displayPercent - DISPLAY_MIN) / displaySpan) * (100 - actualMin);
    }
    const displaySpan = DISPLAY_MAX - 100;
    if (displaySpan <= 0) return 100;
    return 100 + ((displayPercent - 100) / displaySpan) * (actualMax - 100);
  }

  mapRange(x, min_output = 2, max_output = 40, min_input = 300, max_input = 15000) {
    return ((x - min_input) / (max_input - min_input)) * (max_output - min_output) + min_output;
  }

  calculateStrokeWidth() {
    const img = this.canvasFabric.backgroundImage;
    if (!img) return 4; // Default fallback

    const imgWidth = img.width;
    const imgHeight = img.height;

    const maxDimension = Math.max(imgWidth, imgHeight);

    const baseWidth = Math.floor(this.mapRange(maxDimension));

    let width = baseWidth;

    width = Math.min(40, Math.max(1, width));

    return width;
  }

  calculateTriangleSize() {
    const img = this.canvasFabric.backgroundImage;
    if (!img) return 20;

    const imgWidth = img.width;
    const imgHeight = img.height;

    const maxDimension = Math.max(imgWidth, imgHeight);

    let baseSize = Math.floor(this.mapRange(maxDimension, 10, 200, 300, 15000));

    baseSize = Math.min(200, Math.max(10, baseSize));

    return baseSize;
  }

  // Move image and objects accordingly
  enableImageMove() {
    this.canvasEventGroups.clear('move');
    this.canvasEventGroups.add('move', 'mouse:down', this.imageMoveMouseDownHandler);
    this.canvasEventGroups.add('move', 'mouse:move', this.imageMoveMouseMoveHandler);
    this.canvasEventGroups.add('move', 'mouse:up', this.imageMoveMouseUpHandler);
  }

  // Zoom and unzoom image and objects
  enableImageZoom() {
    this.canvasEventGroups.clear('zoom');
    this.canvasEventGroups.add('zoom', 'mouse:wheel', this.imageZoomMouseWheelHandler);
  }

  toggleImageMove() {
    this.isImageMovable = !this.isImageMovable;
  }

  toggleAnnotationsDisplay() {
    this.isAnnotationDisplayEnabled = !this.isAnnotationDisplayEnabled;
    if (!this.isAnnotationDisplayEnabled) {
      this.drawingHandler.resetMode(false);
    } else {
      this.drawingHandler.resetMode();
      if (this.activeAnnotationMessage) {
        // TODO: Tie to the asynchronous drawings add function, currently not working
        this.drawingHandler.highlightAnnotation(this.activeAnnotationMessage.id);
      }
    }
  }

  resize(width: number, height: number, resetViewpoint: boolean = false) {
    if (!this.handlerReady) return;

    const { canvasFabric } = this;
    canvasFabric.setDimensions({ width: width, height: height });
    this.centerViewport();
    this.setCanvasZoom(this.canvasZoom);
    if (resetViewpoint) {
      this.centerImage(true);
      // this.resetImageOrientation();
    }
  }

  registerListeners() {
    this.annotationPlugin.registerListener('enteredAddingAnnotation', (event: any) => {
      this.drawingHandler.startMouseEvents();
    });

    this.annotationPlugin.registerListener('exitingAddingAnnotation', (event: any) => {
      this.drawingHandler.resetMode();
    });

    this.annotationPlugin.registerListener('channelSubscribed', (event: any) => {
      this.drawingHandler.resetMode();
    });

    this.annotationPlugin.registerListener('annotationMessagesLoaded', (event: any) => {
      this.drawingHandler.resetMode();
    });

    this.annotationPlugin.registerListener('enteredEditingAnnotation', (event: any) => {
      this.drawingHandler.resetMode(false);
      this.drawingHandler.addAnnotationDrawings(event.detail.annotation.id, event.detail.annotation.shape, false);
      this.drawingHandler.startMouseEvents();
    });

    this.annotationPlugin.registerListener('exitingEditingAnnotation', (event: any) => {
      this.drawingHandler.resetMode();
      onExitingEditingAnnotation.emit({});
    });

    this.annotationPlugin.registerListener('annotationDeleted', (event: any) => {
      this.drawingHandler.removeAnnotationDrawings(event.detail.id);
    });

    this.annotationPlugin.registerListener('annotationHoveredIn', (event: any) => {
      this.drawingHandler.highlightAnnotation(event.detail.annotation.id);
    });
    this.annotationPlugin.registerListener('annotationHoveredOut', (event: any) => {
      this.drawingHandler.unhighlightAnnotation(event.detail.annotation.id);
    });
    this.annotationPlugin.registerListener('annotationClickedIn', (event: any) => {
      this.drawingHandler.disableAnnotationHover();
      this.drawingHandler.highlightAnnotation(event.detail.annotation.id, true);
      this.activeAnnotationMessage = event.detail.annotation;
    });
    this.annotationPlugin.registerListener('annotationClickedOut', (event: any) => {
      this.drawingHandler.unhighlightAnnotation(event.detail.annotation.id, true);
      this.drawingHandler.enableAnnotationHover();
      this.activeAnnotationMessage = null;
    });
    this.annotationPlugin.registerListener('onStateChanged', (event: any) => {
      onAnnotationsStateChanged.emit({});
    });
    this.annotationPlugin.registerListener('annotationCreated', (event: any) => {
      if (!this.isAnnotatingOrEditing) this.drawingHandler.resetMode(true);
    });

    this.annotationPlugin.registerListener('annotationEdited', (event: any) => {
      if (!this.isAnnotatingOrEditing) this.drawingHandler.resetMode(true);
    });

    this.annotationPlugin.registerListener('enableAnnotationDrawing', () => {
      this.drawingHandler?.enableDrawingInteractions();
      this.isImageMovable = false;
    });

    this.annotationPlugin.registerListener('disableAnnotationDrawing', () => {
      this.drawingHandler?.disableDrawingInteractions();
      this.isImageMovable = true;
    });

    EmitterService.get('changeActiveTool')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: any) => {
        this.drawingHandler.changeActiveTool(event.tool);
      });

    EmitterService.get('changeColor')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: any) => {
        this.drawingHandler.changeColor(event.color);
      });

    onAnnotationDirtyStateChanged.pipe(takeUntil(this.ngUnsubscribe)).subscribe(({ dirty, instanceId }) => {
      if (instanceId !== this.instanceId) {
        return;
      }
      this.hasUnsavedDrawingState = !!dirty;
    });
  }

  saveAnnotation(annotationComment: string, aiParams: AiOptions) {
    const shape = this.drawingHandler.getCurrentShapes();

    const annotationParams: AnnotationParams = {
      shape: JSON.stringify(shape),
      annotationComment: annotationComment,
      start: null,
      end: null,
      isGeneralComment: false,
      privateConditionKey: null,
      agentVersionId: aiParams.agentVersionId ?? null,
      mediaQuality: aiParams.mediaQuality ?? null,
      // privateConditionKey: this.plugin.primordial.privateCondition
    };
    this.annotationPlugin.fire('saveNewAnnotation', { annotationParams: annotationParams });
  }

  saveEditedAnnotation(annotationComment: string, aiParams: AiOptions) {
    const resolution = {
      width: this.canvasFabric.backgroundImage.width,
      height: this.canvasFabric.backgroundImage.height,
    };
    const shape = this.drawingHandler.getCurrentShapes();

    const annotationParams: AnnotationParams = {
      shape: JSON.stringify(shape),
      annotationComment: annotationComment,
      resolution: resolution,
      agentVersionId: aiParams.agentVersionId ?? null,
      mediaQuality: aiParams.mediaQuality ?? null,
      // privateConditionKey: this.plugin.primordial.privateCondition
    };
    this.annotationPlugin.fire('saveEditedAnnotation', { annotationParams: annotationParams });
  }

  deleteCommentAnnotation(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.item.id) {
      return;
    }
    this.annotationPlugin.fire('destroyComment', { id: args.comment.id, annotation: args.annotation });
  }

  teardown() {
    this.canvasEventGroups.clearAll();
    this.drawingHandler?.teardown();
    this.annotationPlugin.fire('dispose');
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }
}
